import { marked, type TokenizerAndRendererExtension } from 'marked'
import TurndownService from 'turndown'

// ── HumanId inline extension ───────────────────────────────────────
// Matches :t123456, :d100001, :m100002, :g100003, :p100004 patterns in markdown
// and renders them as styled, clickable badges.

const humanIdColorMap: Record<string, string> = {
  t: 'var(--v-theme-primary)',
  d: '#7e57c2',
  m: '#009688',
  g: '#ff8f00',
  p: '#546e7a',
}

const humanIdLabelMap: Record<string, string> = {
  t: 'Задача',
  d: 'Направление',
  m: 'Мероприятие',
  g: 'Цель',
  p: 'Проект',
}

const humanIdIconMap: Record<string, string> = {
  t: 'mdi-checkbox-marked-outline',
  d: 'mdi-compass-outline',
  m: 'mdi-clipboard-check-outline',
  g: 'mdi-flag-outline',
  p: 'mdi-folder-outline',
}

const humanIdRouteMap: Record<string, string> = {
  t: '/tasks/',
  d: '/strategy/directions/',
  m: '/strategy/measures/',
  p: '/projects/',
}

const humanIdExtension: TokenizerAndRendererExtension = {
  name: 'humanId',
  level: 'inline',
  start(src: string) {
    return src.indexOf(':')
  },
  tokenizer(src: string) {
    const match = src.match(/^:([tdmgp])(\d{6,})/)
    if (match) {
      return {
        type: 'humanId',
        raw: match[0],
        prefix: match[1],
        digits: match[2],
        humanId: match[0],
      }
    }
    return undefined
  },
  renderer(token) {
    const { prefix, humanId } = token as unknown as { prefix: string; humanId: string }
    const color = humanIdColorMap[prefix] || humanIdColorMap.t
    const label = humanIdLabelMap[prefix] || humanIdLabelMap.t
    const icon = humanIdIconMap[prefix] || humanIdIconMap.t
    const route = humanIdRouteMap[prefix]
    const tag = route ? 'a' : 'span'
    const href = route ? ` href="${route}${humanId}"` : ''

    return `<${tag}${href} class="human-id-mention human-id-mention--${prefix}" data-human-id="${humanId}" data-prefix="${prefix}" title="${label}" style="--hid-color: ${color}"><i class="mdi ${icon}"></i>${humanId}</${tag}>`
  },
}

marked.use({ extensions: [humanIdExtension] })

// Единый экземпляр turndown с настройками проекта
const turndown = new TurndownService({
  headingStyle: 'atx',
  codeBlockStyle: 'fenced',
  bulletListMarker: '-',
})

// GFM таблицы
turndown.addRule('table', {
  filter: ['table'],
  replacement(_content, node) {
    const table = node as HTMLTableElement
    const rows = Array.from(table.querySelectorAll('tr'))
    if (rows.length === 0) return ''

    const result: string[] = []
    rows.forEach((row, i) => {
      const cells = Array.from(row.querySelectorAll('th, td'))
      const line = cells.map(cell => cell.textContent?.trim() || '').join(' | ')
      result.push(`| ${line} |`)

      if (i === 0) {
        const sep = cells.map(() => '---').join(' | ')
        result.push(`| ${sep} |`)
      }
    })

    return '\n\n' + result.join('\n') + '\n\n'
  },
})

// GFM зачёркнутый текст.
// Ядро turndown не знает про <s>/<del>/<strike> — без этого правила теги
// просто выбрасывались, а текст сохранялся обычным. Со стороны пользователя
// это выглядело как «зачёркивание не сохраняется»: marked при отрисовке
// понимает ~~текст~~ (gfm включён по умолчанию), а обратная конвертация
// HTML → Markdown теряла разметку при каждом сохранении.
turndown.addRule('strikethrough', {
  filter: ['del', 's', 'strike' as keyof HTMLElementTagNameMap],
  replacement(content) {
    // Пустое выделение не заворачиваем — иначе получим осиротевшие «~~~~»
    return content.trim() ? `~~${content}~~` : content
  },
})

// HumanId badges → back to :tXXXXXX text
turndown.addRule('humanIdMention', {
  filter(node) {
    return (node.nodeName === 'A' || node.nodeName === 'SPAN') &&
      (node as HTMLElement).classList.contains('human-id-mention')
  },
  replacement(_content, node) {
    return (node as HTMLElement).getAttribute('data-human-id') || _content
  },
})

// Пустые абзацы. Абзац с одной картинкой textContent не имеет, но пустым не
// является — иначе картинка, вставленная отдельной строкой, пропадает при сохранении
turndown.addRule('emptyParagraph', {
  filter(node) {
    return (
      node.nodeName === 'P' &&
      (!node.textContent || node.textContent.trim() === '') &&
      !node.querySelector('img, picture, video, audio, iframe, svg')
    )
  },
  replacement() {
    return '\n\n'
  },
})

/**
 * Post-process HTML to convert humanId text patterns in already-rendered HTML.
 * Skips content inside <code> and <pre> tags.
 */
function postProcessHumanIds(html: string): string {
  // Split by code/pre tags to avoid replacing inside them
  return html.replace(/(<code[\s>][\s\S]*?<\/code>|<pre[\s>][\s\S]*?<\/pre>)|:([tdmgp])(\d{6,})/gi,
    (match, codeBlock, prefix, digits) => {
      if (codeBlock) return codeBlock
      if (!prefix) return match
      const p = prefix.toLowerCase()
      const humanId = `:${p}${digits}`
      const color = humanIdColorMap[p] || humanIdColorMap.t
      const label = humanIdLabelMap[p] || humanIdLabelMap.t
      const icon = humanIdIconMap[p] || humanIdIconMap.t
      const route = humanIdRouteMap[p]
      const tag = route ? 'a' : 'span'
      const href = route ? ` href="${route}${humanId}"` : ''
      return `<${tag}${href} class="human-id-mention human-id-mention--${p}" data-human-id="${humanId}" data-prefix="${p}" title="${label}" style="--hid-color: ${color}"><i class="mdi ${icon}"></i>${humanId}</${tag}>`
    })
}

/**
 * Конвертация Markdown → HTML
 */
export function mdToHtml(md: string): string {
  if (!md) return ''
  if (isHtml(md)) return postProcessHumanIds(md)
  return marked.parse(md, { async: false }) as string
}

/**
 * Содержимое поля → HTML для загрузки в редактор (TipTap).
 * Поля хранятся как вывод htmlToMd, поэтому всё, что не HTML, — markdown.
 * Угадывать markdown по «признакам» нельзя: текст без заголовков/списков
 * (картинки с пустым alt, экранированная нумерация `1\.`) уходил в редактор
 * как HTML-текст — переводы строк схлопывались, а при сохранении turndown
 * экранировал разметку (`!\[\](…)`).
 */
export function markdownToEditorHtml(content: string): string {
  if (!content) return ''
  if (isHtml(content)) return content
  return marked.parse(content, { async: false }) as string
}

/**
 * Конвертация HTML → Markdown
 */
export function htmlToMd(html: string): string {
  if (!html) return ''
  if (!isHtml(html)) return html
  return turndown.turndown(html)
}

/**
 * Определяет, является ли строка HTML (а не Markdown).
 * Не считает HTML-теги внутри code-блоков markdown.
 */
export function isHtml(content: string): boolean {
  if (!content) return false
  const trimmed = content.trim()
  // Начинается с HTML-тега — однозначно HTML
  if (/^<(?:p|div|h[1-6]|ul|ol|li|table|blockquote|pre|br|hr|!doctype|html|head|body)\b/i.test(trimmed)) {
    return true
  }
  // Убираем code-блоки чтобы не матчить теги внутри кода
  const withoutCode = trimmed
    .replace(/```[\s\S]*?```/g, '')
    .replace(/`[^`\n]+`/g, '')
  // Ищем блочные HTML-теги в оставшемся тексте
  return /<(?:p|div|h[1-6]|ul|ol|li|table|blockquote|pre|br|hr)[\s>\/]/i.test(withoutCode)
}

/**
 * Рендерит Markdown или HTML для отображения в v-html
 * Всегда возвращает HTML для рендеринга
 */
export function renderMarkdown(content: string): string {
  if (!content) return ''
  return mdToHtml(content)
}
